import React from 'react';
import { Mail, Phone, Building2, MapPin, FileText, Anchor } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { countries } from '@/data/countries';

// US states data
interface USState {
  name: string;
  code: string;
}

const usStates: USState[] = [
  { name: "Alabama", code: "AL" },
  { name: "Alaska", code: "AK" },
  { name: "Arizona", code: "AZ" },
  { name: "Arkansas", code: "AR" },
  { name: "California", code: "CA" },
  { name: "Colorado", code: "CO" },
  { name: "Connecticut", code: "CT" },
  { name: "Delaware", code: "DE" },
  { name: "Florida", code: "FL" },
  { name: "Georgia", code: "GA" },
  { name: "Hawaii", code: "HI" },
  { name: "Idaho", code: "ID" },
  { name: "Illinois", code: "IL" },
  { name: "Indiana", code: "IN" },
  { name: "Iowa", code: "IA" },
  { name: "Kansas", code: "KS" },
  { name: "Kentucky", code: "KY" },
  { name: "Louisiana", code: "LA" },
  { name: "Maine", code: "ME" },
  { name: "Maryland", code: "MD" },
  { name: "Massachusetts", code: "MA" },
  { name: "Michigan", code: "MI" },
  { name: "Minnesota", code: "MN" },
  { name: "Mississippi", code: "MS" },
  { name: "Missouri", code: "MO" },
  { name: "Montana", code: "MT" },
  { name: "Nebraska", code: "NE" },
  { name: "Nevada", code: "NV" },
  { name: "New Hampshire", code: "NH" },
  { name: "New Jersey", code: "NJ" },
  { name: "New Mexico", code: "NM" },
  { name: "New York", code: "NY" },
  { name: "North Carolina", code: "NC" },
  { name: "North Dakota", code: "ND" },
  { name: "Ohio", code: "OH" },
  { name: "Oklahoma", code: "OK" },
  { name: "Oregon", code: "OR" },
  { name: "Pennsylvania", code: "PA" },
  { name: "Rhode Island", code: "RI" },
  { name: "South Carolina", code: "SC" },
  { name: "South Dakota", code: "SD" },
  { name: "Tennessee", code: "TN" },
  { name: "Texas", code: "TX" },
  { name: "Utah", code: "UT" },
  { name: "Vermont", code: "VT" },
  { name: "Virginia", code: "VA" },
  { name: "Washington", code: "WA" },
  { name: "West Virginia", code: "WV" },
  { name: "Wisconsin", code: "WI" },
  { name: "Wyoming", code: "WY" },
  { name: "District of Columbia", code: "DC" }
];


// Phone number formatter
const formatPhoneNumber = (input: string): string => {
  // Strip all non-numeric characters
  const cleaned = input.replace(/\D/g, '');

  // Check if it's a US number (starts with 1 or has 10 digits)
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    // Format as US number: +1 (XXX) XXX-XXXX
    const countryCode = cleaned.length === 11 ? cleaned.substring(0, 1) : '1';
    const areaCode = cleaned.substring(cleaned.length - 10, cleaned.length - 7);
    const firstPart = cleaned.substring(cleaned.length - 7, cleaned.length - 4);
    const lastPart = cleaned.substring(cleaned.length - 4);
    return `+${countryCode} (${areaCode}) ${firstPart}-${lastPart}`;
  }

  // If not a valid US number, return the cleaned input
  return input;
};

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName?: string;
  street: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  notes: string;
  cabinNumber?: string;
  hostName?: string;
}

export interface CustomerFormFieldsProps {
  formData: CustomerFormData;
  onChange: (field: keyof CustomerFormData, value: string) => void;
  onAddressChange: (address: { street: string; city: string; state: string; zipCode: string }) => void;
  showCruiseFields?: boolean;
}

export function CustomerFormFields({
  formData,
  onChange,
  onAddressChange,
  showCruiseFields = false,
}: CustomerFormFieldsProps) {
  const showStateDropdown = formData.country === 'United States';

  const handlePhoneBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (formData.country === 'United States') {
      const formattedPhone = formatPhoneNumber(e.target.value);
      onChange('phone', formattedPhone);
    }
  };

  const handleAddressAutocompleteChange = (address: { street: string; city: string; state: string; zipCode: string }) => {
    // Convert state code (e.g., "CA") to state name (e.g., "California")
    const stateCode = address.state?.toUpperCase() || '';
    const stateName = usStates.find(s => s.code === stateCode)?.name || stateCode;

    onAddressChange({
      street: address.street || '',
      city: address.city || '',
      state: stateName,
      zipCode: address.zipCode || '',
    });
  };

  return (
    <>
      {/* Personal Information Section */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName" className="text-sm font-medium flex items-center gap-1">
              First Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              className="mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="lastName" className="text-sm font-medium flex items-center gap-1">
              Last Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              className="mt-1.5"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
              <Mail className="h-4 w-4" /> Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => onChange('email', e.target.value)}
              className="mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
              <Phone className="h-4 w-4" /> Phone <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => onChange('phone', e.target.value)}
              onBlur={handlePhoneBlur}
              className="mt-1.5"
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="companyName" className="text-sm font-medium flex items-center gap-2">
            <Building2 className="h-4 w-4" /> Company Name
          </Label>
          <Input
            id="companyName"
            name="companyName"
            value={formData.companyName}
            onChange={(e) => onChange('companyName', e.target.value)}
            className="mt-1.5"
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Address Section */}
      <div className="space-y-4 pt-2">
        <h3 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4" />
          Address Information
        </h3>

        <div>
          <AddressAutocomplete
            label="Street Address"
            placeholder="Start typing address..."
            required
            value={{
              street: formData.street || '',
              city: formData.city || '',
              state: formData.state || '',
              zipCode: formData.zip || '',
              fullAddress: formData.street
                ? `${formData.street}${formData.city ? ', ' + formData.city : ''}${formData.state ? ', ' + formData.state : ''}${formData.zip ? ' ' + formData.zip : ''}`
                : ''
            }}
            onChange={handleAddressAutocompleteChange}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city" className="text-sm font-medium flex items-center gap-1">
              City <span className="text-red-500">*</span>
            </Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={(e) => onChange('city', e.target.value)}
              className="mt-1.5"
              required
            />
          </div>
          <div>
            <Label htmlFor="country" className="text-sm font-medium flex items-center gap-1">
              Country <span className="text-red-500">*</span>
            </Label>
            <Select
              name="country"
              value={formData.country}
              onValueChange={(value) => {
                onChange('country', value);
                if (value !== 'United States') {
                  onChange('state', '');
                }
              }}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map(country => (
                  <SelectItem key={country.name} value={country.name}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {showStateDropdown ? (
            <div>
              <Label htmlFor="state" className="text-sm font-medium flex items-center gap-1">
                State <span className="text-red-500">*</span>
              </Label>
              <Select
                name="state"
                value={formData.state}
                onValueChange={(value) => onChange('state', value)}
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select a state" />
                </SelectTrigger>
                <SelectContent>
                  {usStates.map(state => (
                    <SelectItem key={state.code} value={state.name}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label htmlFor="state" className="text-sm font-medium flex items-center gap-1">
                State/Province <span className="text-red-500">*</span>
              </Label>
              <Input
                id="state"
                name="state"
                value={formData.state}
                onChange={(e) => onChange('state', e.target.value)}
                className="mt-1.5"
                required
              />
            </div>
          )}
          <div>
            <Label htmlFor="zip" className="text-sm font-medium flex items-center gap-1">
              ZIP / Postal Code <span className="text-red-500">*</span>
            </Label>
            <Input
              id="zip"
              name="zip"
              value={formData.zip}
              onChange={(e) => onChange('zip', e.target.value)}
              className="mt-1.5"
              required
            />
          </div>
        </div>
      </div>

      {/* Cruise Ship Fields */}
      {showCruiseFields && (
        <div className="space-y-4 pt-2">
          <h3 className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
            <Anchor className="h-4 w-4" />
            Cruise Ship Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cabinNumber" className="text-sm font-medium">
                Cabin Number
              </Label>
              <Input
                id="cabinNumber"
                name="cabinNumber"
                value={formData.cabinNumber}
                onChange={(e) => onChange('cabinNumber', e.target.value)}
                className="mt-1.5"
                placeholder="Optional"
              />
            </div>
            <div>
              <Label htmlFor="hostName" className="text-sm font-medium">
                Host Name
              </Label>
              <Input
                id="hostName"
                name="hostName"
                value={formData.hostName}
                onChange={(e) => onChange('hostName', e.target.value)}
                className="mt-1.5"
                placeholder="Optional"
              />
            </div>
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="space-y-4 pt-2">
        <Label htmlFor="notes" className="text-sm font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" /> Notes
        </Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          className="h-20 resize-none mt-1.5"
          placeholder="Add any additional notes about the customer..."
        />
      </div>
    </>
  );
}
